import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const inputCls =
  "mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]";

/** Create-artist form shared by the center dashboard and admin panel.
 *  `createFn(payload)` posts to the right endpoint; `members` lists existing ones. */
export const CenterArtistsPanel = ({ members = [], createFn, onCreated }) => {
  const [form, setForm] = useState({ name: "", email: "", country: "", bio: "" });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null); // last created artist (may hold temp_password)

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await createFn({
        name: form.name.trim(),
        email: form.email.trim() || null,
        country: form.country.trim() || null,
        bio: form.bio.trim() || null,
      });
      setCreated(data);
      setForm({ name: "", email: "", country: "", bio: "" });
      toast.success(`Artist "${data.name}" created`);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create artist");
    } finally {
      setSubmitting(false);
    }
  };

  const ownArtists = members.filter((m) => m.created_by_center);

  return (
    <div data-testid="center-artists-panel">
      <div className="border border-[#E5E0D8] bg-white p-6">
        <p className="label-eyebrow text-[#1E3A8A]">— Add an artist</p>
        <p className="text-sm text-[#555] mt-2 max-w-2xl">
          With an email, a full artist account is created (you'll get a one-time temporary
          password to share with them). Without an email, a profile-only artist is created
          that you manage entirely.
        </p>
        <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label-eyebrow text-[#555]">Artist name *</label>
            <Input data-testid="center-artist-name" required value={form.name} onChange={set("name")} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Email (optional — enables login)</label>
            <Input data-testid="center-artist-email" type="email" value={form.email} onChange={set("email")} placeholder="artist@example.com" className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Country</label>
            <Input data-testid="center-artist-country" value={form.country} onChange={set("country")} placeholder="e.g. Zimbabwe" className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow text-[#555]">Short bio</label>
            <Textarea data-testid="center-artist-bio" rows={3} value={form.bio} onChange={set("bio")} className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#111]" />
          </div>
          <div className="md:col-span-2">
            <Button
              type="submit"
              data-testid="center-artist-submit"
              disabled={submitting}
              className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-8 py-3 h-auto label-eyebrow"
            >
              {submitting ? "Creating…" : "Create artist"}
            </Button>
          </div>
        </form>

        {created?.temp_password && (
          <div data-testid="center-artist-temp-password" className="mt-6 border-l-2 border-[#F4C430] bg-[#F4EFE6]/40 px-4 py-3">
            <p className="label-eyebrow text-[#1E3A8A]">— One-time credentials for {created.name}</p>
            <p className="text-sm text-[#333] mt-2">
              Email: <span className="font-mono-aam">{created.email}</span> · Temporary password:{" "}
              <span className="font-mono-aam text-[#1E3A8A] font-bold">{created.temp_password}</span>
            </p>
            <p className="text-xs text-[#555] mt-1">
              Copy this now — it won't be shown again. The artist can sign in and change it from their profile.
            </p>
          </div>
        )}
        {created && !created.temp_password && (
          <div data-testid="center-artist-managed-note" className="mt-6 border-l-2 border-[#F4C430] bg-[#F4EFE6]/40 px-4 py-3">
            <p className="text-sm text-[#333]">
              <span className="label-eyebrow text-[#1E3A8A]">Profile-only · </span>
              {created.name} has no login — you manage their profile and works from this dashboard.
            </p>
          </div>
        )}
      </div>

      {ownArtists.length > 0 && (
        <div className="mt-8">
          <p className="label-eyebrow text-[#555]">— Artists created by your hub</p>
          <div className="mt-4 border border-[#E5E0D8] bg-white">
            {ownArtists.map((m) => (
              <div key={m.artist_id} data-testid={`center-own-artist-${m.artist_id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-[#E5E0D8] last:border-0">
                <div>
                  <Link to={`/artist/${m.artist_id}`} className="font-serif-display text-lg text-[#1E3A8A] hover:underline">
                    {m.artist_name}
                  </Link>
                  <p className="text-xs text-[#555]">{m.artist?.country || "—"} · {m.artwork_count} work{m.artwork_count === 1 ? "" : "s"}</p>
                </div>
                <span className="label-eyebrow text-xs text-[#555]">
                  {m.artist?.publish_status === "draft" ? "Awaiting admin publish" : "Published"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
